import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  Paper,
  Typography,
  Box,
  Grid,
  Stack,
  Alert,
  Divider,
  Avatar,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import axios from "axios";
import { API_URL, API_UPLOAD_URL } from "./config";

import SchoolIcon from "@mui/icons-material/School";
import SearchIcon from "@mui/icons-material/Search";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PersonIcon from "@mui/icons-material/Person";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import BadgeIcon from "@mui/icons-material/Badge";
import HomeIcon from "@mui/icons-material/Home";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import BloodtypeIcon from "@mui/icons-material/Bloodtype";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import NumbersIcon from "@mui/icons-material/Numbers";
import ApartmentIcon from "@mui/icons-material/Apartment";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const THEME_ORANGE = "#F07828";
const THEME_BLUE = "#004D99";
const SOFT_BLUE = "#EAF4FF";
const PAGE_BG = "#F2F2F2";
const BORDER = "rgba(0,77,153,0.16)";

const initialFormData = {
  ApplicationID: "",
  CampusCode: "",
  SubCampusCode: "",
  DegreeCode: "",
  BranchCode: "",
  AcademicYear: "",
  FirstName: "",
  FamilyName: "",
  Gender: "",
  BloodGroup: "",
  DateOfBirth: "",
  AddressLine1: "",
  AddressLine2: "",
  PostOfficeName: "",
  City: "",
  District: "",
  State: "",
  Zip: "",
  IsHostelFacilityRequired: "No",
  AadharNumber: "",
  PhoneNumber: "",
  AlternatePhoneNumber: "",
  EmailAddress: "",
  Religion: "",
  Caste: "",
  SubCaste: "",
  FatherFullName: "",
  FatherAadharNumber: "",
  FatherQualification: "",
  FatherOccupation: "",
  FatherPhoneNumber: "",
  MotherFullName: "",
  MotherAadharNumber: "",
  MotherQualification: "",
  MotherOccupation: "",
  MotherPhoneNumber: "",
  ReferenceName: "",
  ReferencePhoneNumber: "",
};

const emptyEducationRow = {
  EducationLevel: "",
  BoardType: "",
  InstitutionName: "",
  HallTicketNumber: "",
  Percentage: "",
  CGPA: "",
  InstitutionCity: "",
  InstitutionState: "",
  PassedOutYear: "",
};

const emptyExamRow = {
  ExamType: "",
  HallTicketNumber: "",
  RankNo: "",
  Score: "",
  ExamYear: "",
};

const indianStates = [
  "Andaman and Nicobar Islands",
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chandigarh",
  "Chhattisgarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jammu and Kashmir",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Ladakh",
  "Lakshadweep",
  "Maharashtra",
  "Madhya Pradesh",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Puducherry",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Others",
];

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    backgroundColor: "#fff",
    "& fieldset": {
      borderColor: BORDER,
    },
    "&:hover fieldset": {
      borderColor: "rgba(0,77,153,0.35)",
    },
    "&.Mui-focused fieldset": {
      borderColor: THEME_ORANGE,
      borderWidth: 2,
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: THEME_ORANGE,
  },
};

const selectSx = {
  borderRadius: 2,
  backgroundColor: "#fff",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: BORDER,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(0,77,153,0.35)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: THEME_ORANGE,
    borderWidth: 2,
  },
};

const formatDateForInput = (value) => {
  if (!value) return "";

  const str = String(value);

  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;

  const isoMatch = str.match(/^(\d{4}-\d{2}-\d{2})T/);
  if (isoMatch) return isoMatch[1];

  return "";
};

function SectionHeader({ icon, title, subtitle }) {
  return (
    <Grid item xs={12}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
        <Avatar
          sx={{
            bgcolor: SOFT_BLUE,
            color: THEME_BLUE,
            width: 38,
            height: 38,
            border: `1px solid ${BORDER}`,
          }}
        >
          {icon}
        </Avatar>
        <Box>
          <Typography sx={{ color: THEME_BLUE, fontWeight: 900, fontSize: 17 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
      <Divider sx={{ mt: 1.5, borderColor: BORDER }} />
    </Grid>
  );
}

function StateSelect({ label, name, value, onChange, required }) {
  return (
    <FormControl fullWidth required={required} sx={inputSx}>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        name={name}
        value={value}
        onChange={onChange}
        sx={selectSx}
      >
        {indianStates.map((state) => (
          <MenuItem key={state} value={state}>
            {state}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

const FormStudentApplication = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [passportPhoto, setPassportPhoto] = useState(null);
  const [passportPreview, setPassportPreview] = useState(null);

  const [errorstate, setErrorState] = useState({});
  const [branchCodes, setBranchCodes] = useState([]);
  const [degreeCodes, setDegreeCodes] = useState([]);
  const [subcampusCodes, setSubCampusCodes] = useState([]);
  const [campusError, setCampusError] = useState("");
  const [success, setSuccess] = useState("");
  const [retrievedApplication, setRetrievedApplication] = useState(false);
  const [lookupAcademicYears, setLookupAcademicYears] = useState([]);
  const [currentAcademicYear, setCurrentAcademicYear] = useState("");
  const [loginCampusCode, setLoginCampusCode] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("auth");
  
    if (!token) return;
  
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const cc = payload.campusCode || payload.CampusCode || "";
  
      setLoginCampusCode(cc);
  
      setFormData((prev) => ({
        ...prev,
        CampusCode: cc,
      }));
    } catch (err) {
      console.error("Failed to decode token:", err);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("auth");
  
    if (!token) return;
  
    const fetchLookups = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/lookups/all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
  
        const lookups = res.data || {};
  
        setSubCampusCodes(
          (lookups.SubCampusCode || []).map((v) => ({ SubCampusCode: v }))
        );
  
        setDegreeCodes(
          (lookups.DegreeCode || []).map((v) => ({ DegreeCode: v }))
        );
  
        setBranchCodes(
          (lookups.BranchCode || []).map((v) => ({ BranchCode: v }))
        );
  
        setLookupAcademicYears(lookups.AcademicYear || []);
  
        const currentYear = lookups.CurrentAcademicYear?.[0] || "";
  
        setCurrentAcademicYear(currentYear);
  
        setFormData((prev) => ({
          ...prev,
          CampusCode: prev.CampusCode || loginCampusCode,
          //AcademicYear: prev.AcademicYear || currentYear,
        }));
      } catch (err) {
        console.error("Failed to fetch lookups:", err);
      }
    };
  
    fetchLookups();
  }, [loginCampusCode]);

  const [retrieveData, setRetrieveData] = useState({
    ApplicationID: "",
    DateOfBirth: "",
    PhoneNumber: "",
  });

  const [retrieveError, setRetrieveError] = useState("");
  const [retrieveSuccess, setRetrieveSuccess] = useState("");

  const generateApplicationID = (
    campusCode,
    subCampusCode,
    degreeCode,
    branchCode
  ) => {
    if (!campusCode || !subCampusCode || !degreeCode || !branchCode) return "";
    const year = new Date().getFullYear();
    const randomNumber = Math.floor(100000 + Math.random() * 900000);
    return `${year}${subCampusCode}${degreeCode}${branchCode}${randomNumber}`;
  };

  const [educationDetails, setEducationDetails] = useState([
    { ...emptyEducationRow, EducationLevel: "Tenth" },
    { ...emptyEducationRow, EducationLevel: "Intermediate" },
  ]);

  const [competitiveExams, setCompetitiveExams] = useState([{ ...emptyExamRow }]);

  const [pincodeRecords, setPincodeRecords] = useState([]);
  const [isCustomLocality, setIsCustomLocality] = useState(false);

  const handleEducationChange = (index, field, value) => {
    setEducationDetails((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const addEducationRow = () => {
    setEducationDetails((prev) => [...prev, { ...emptyEducationRow }]);
  };

  const removeEducationRow = (index) => {
    setEducationDetails((prev) => prev.filter((_, i) => i !== index));
  };

  const handleExamChange = (index, field, value) => {
    setCompetitiveExams((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const addExamRow = () => {
    setCompetitiveExams((prev) => [...prev, { ...emptyExamRow }]);
  };

  const removeExamRow = (index) => {
    setCompetitiveExams((prev) => prev.filter((_, i) => i !== index));
  };

  const fetchPincodeDetails = async (pincode) => {
    if (!/^\d{6}$/.test(pincode)) {
      setPincodeRecords([]);
      setIsCustomLocality(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/pincode/${pincode}`);
      const records = response.data?.records || [];

      if (response.data?.success && records.length > 0) {
        setPincodeRecords(records);
        setIsCustomLocality(false);

        setFormData((prev) => ({
          ...prev,
          PostOfficeName: "",
          District: records[0]?.DistrictName || "",
          State: records[0]?.StateName || "",
          // City should remain user-entered
          City: prev.City || "",
        }));
      } else {
        setPincodeRecords([]);
        setIsCustomLocality(true);

        setFormData((prev) => ({
          ...prev,
          PostOfficeName: "",
          District: "",
          State: "",
          City: prev.City || "",
        }));
      }
    } catch (error) {
      console.error("Error fetching pincode:", error);
      setPincodeRecords([]);
      setIsCustomLocality(true);
    }
  };

  const handleLocalitySelect = (recordKey) => {
    if (recordKey === "__ADD_NEW__") {
      setIsCustomLocality(true);

      setFormData((prev) => ({
        ...prev,
        City: "",
        District: "",
        State: "",
      }));

      return;
    }

    const selected = pincodeRecords.find(
      (r) => String(r.PostalCodeID) === String(recordKey)
    );

    if (!selected) return;

    setIsCustomLocality(false);

    setFormData((prev) => ({
      ...prev,
      City: selected.PostOfficeName || "",
      District: selected.DistrictName || "",
      State: selected.StateName || "",
    }));
  };

  const saveNewLocalityIfNeeded = async () => {
    if (!isCustomLocality) return;

    if (
      !formData.Zip ||
      !formData.PostOfficeName ||
      !formData.District ||
      !formData.State
    ) {
      return;
    }

    try {
      await axios.post(`${API_URL}/api/pincode/add-locality`, {
        Pincode: formData.Zip,
        PostOfficeName: formData.PostOfficeName,
        DistrictName: formData.District,
        StateName: formData.State,
      });
    } catch (error) {
      console.error("Error saving new post office/locality:", error);
    }
  };

  useEffect(() => {
    if (!passportPhoto) return;

    const objectUrl = URL.createObjectURL(passportPhoto);
    setPassportPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [passportPhoto]);

  const validateField = (name, value) => {
    switch (name) {
      case "InterPercentage":
      case "TenthPercentage":
        if (!/^\d*\.?\d*$/.test(value)) {
          return "Only numbers with optional decimals are allowed.";
        }
        if (parseFloat(value) > 100) {
          return "Percentage must not be greater than 100.";
        }
        return "";

      case "DegreePercentage":
        if (value !== "" && !/^\d*\.?\d*$/.test(value)) {
          return "Only numbers with optional decimals are allowed.";
        }
        if (parseFloat(value) > 100) {
          return "Percentage must not be greater than 100.";
        }
        return "";

      case "PhoneNumber":
        if (!/^\d{10}$/.test(value)) {
          return "Phone number must be exactly 10 digits.";
        }
        return "";

      case "AlternatePhoneNumber":
        if (value !== "" && !/^\d{10}$/.test(value)) {
          return "Alternate Phone number must be exactly 10 digits.";
        }
        return "";

      case "FatherPhoneNumber":
        if (!/^\d{10}$/.test(value)) {
          return "Father Phone number must be exactly 10 digits.";
        }
        return "";

      case "MotherPhoneNumber":
        if (!/^\d{10}$/.test(value)) {
          return "Mother Phone number must be exactly 10 digits.";
        }
        return "";

      case "ReferencePhoneNumber":
        if (!/^\d{10}$/.test(value)) {
          return "Reference Phone number must be exactly 10 digits.";
        }
        return "";

      case "AadharNumber":
        if (!/^\d{12}$/.test(value)) {
          return "Aadhar number must be exactly 12 digits.";
        }
        return "";

      case "FatherAadharNumber":
        if (!/^\d{12}$/.test(value)) {
          return "Father Aadhar number must be exactly 12 digits.";
        }
        return "";

      case "MotherAadharNumber":
        if (!/^\d{12}$/.test(value)) {
          return "Mother Aadhar number must be exactly 12 digits.";
        }
        return "";

      case "EAMCETRank":
        if (value !== "" && !/^\d+$/.test(value)) {
          return "EAMCET Rank must be a number.";
        }
        return "";

      case "AIEEERank":
        if (value !== "" && !/^\d+$/.test(value)) {
          return "AIEEE Rank must be a number.";
        }
        return "";

      case "IITRank":
        if (value !== "" && !/^\d+$/.test(value)) {
          return "IIT Rank must be a number.";
        }
        return "";

      default:
        return "";
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    const errorMessage = validateField(name, value);

    setFormData((prevData) => {
      const updatedData = { ...prevData, [name]: value };

      if (name === "SubCampusCode") {
        updatedData.BranchCode = "";
        updatedData.DegreeCode = "";
        updatedData.ApplicationID = "";
      }

      if (name === "DegreeCode") {
        updatedData.BranchCode = "";
        updatedData.ApplicationID = "";
      }

      if (name === "BranchCode") {
        updatedData.ApplicationID = generateApplicationID(
          updatedData.CampusCode,
          updatedData.SubCampusCode,
          updatedData.DegreeCode,
          updatedData.BranchCode
        );
      }

      return updatedData;
    });

    setErrorState((prevErrors) => ({
      ...prevErrors,
      [name]: errorMessage,
    }));

    if (name === "SubCampusCode") {
      try {
        setDegreeCodes([]);
        const response = await axios.get(
          `${API_URL}/api/FSAValidateSubCampus/${formData.CampusCode}/${value}`
        );
        setDegreeCodes(response.data.degrees);
      } catch (error) {
        console.error("Error fetching degrees:", error);
      }
    }

    if (name === "DegreeCode") {
      try {
        setBranchCodes([]);
        const response = await axios.get(
          `${API_URL}/api/FSAValidateDegree/${formData.CampusCode}/${value}`
        );
        setBranchCodes(response.data.branches);
      } catch (error) {
        console.error("Error fetching branches:", error);
      }
    }

    if (name === "Zip") {
      const cleanZip = value.replace(/\D/g, "").slice(0, 6);
    
      setFormData((prev) => ({
        ...prev,
        Zip: cleanZip,
        PostOfficeName: "",
        City: "",
        District: "",
        State: "",
      }));
    
      setPincodeRecords([]);
      setIsCustomLocality(false);
    
      if (cleanZip.length === 6) {
        fetchPincodeDetails(cleanZip);
      }
    
      return;
    }
  };

  const handlePhotoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const maxSize = 2000 * 1024;

      if (file.size > maxSize) {
        alert("Passport photo must be less than 2 MB.");
        return;
      }

      setPassportPhoto(file);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSuccess("");

    const compulsoryFields = [
      "CampusCode",
      "SubCampusCode",
      "DegreeCode",
      "BranchCode",
      "AcademicYear",
      "FirstName",
      "FamilyName",
      "DateOfBirth",
      "PhoneNumber",
    ];

    let errors = {};
    compulsoryFields.forEach((field) => {
      if (!formData[field]) {
        errors[field] = `${field} is required to save progress.`;
      }
    });

    if (Object.keys(errors).length > 0) {
      setErrorState(errors);
      return;
    }

    const finalFormData = {
      ...formData,
      CampusCode: formData.CampusCode || loginCampusCode,
      //AcademicYear: formData.AcademicYear || currentAcademicYear,
    };

    try {
      await saveNewLocalityIfNeeded();
      const submissionData = new FormData();
      Object.keys(finalFormData).forEach((key) => {
        submissionData.append(key, finalFormData[key]);
      });

      if (passportPhoto) {
        submissionData.append("passportPhoto", passportPhoto, passportPhoto.name);
      }

      submissionData.append(
        "EducationDetails",
        JSON.stringify(educationDetails)
      );

      submissionData.append(
        "CompetitiveExams",
        JSON.stringify(competitiveExams)
      );

      const response = await axios.post(
        `${API_URL}/api/SaveStudentApplication`,
        submissionData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 201 || response.status === 200) {
        setSuccess(response.data.message);
      }
    } catch (error) {
      console.error("Error saving application draft:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess("");

    const errors = {};

    Object.keys(formData).forEach((field) => {
      const errorMessage = validateField(field, formData[field]);
      if (errorMessage) errors[field] = errorMessage;
    });

    if (Object.keys(errors).length > 0) {
      setErrorState(errors);
      return;
    }

    const finalFormData = {
      ...formData,
      CampusCode: formData.CampusCode || loginCampusCode,
      //AcademicYear: formData.AcademicYear || currentAcademicYear,
    };

    try {
      await saveNewLocalityIfNeeded();
      const submissionData = new FormData();
      Object.keys(finalFormData).forEach((key) => {
        submissionData.append(key, finalFormData[key]);
      });

      if (passportPhoto) {
        submissionData.append("passportPhoto", passportPhoto, passportPhoto.name);
      }

      submissionData.append(
        "EducationDetails",
        JSON.stringify(educationDetails)
      );

      submissionData.append(
        "CompetitiveExams",
        JSON.stringify(competitiveExams)
      );

      const response = await axios.post(
        `${API_URL}/api/FormStudentApplication`,
        submissionData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 201) {
        setSuccess("Application submitted successfully!");
        setFormData(initialFormData);
        setPassportPhoto(null);
        setPassportPreview(null);
        setErrorState({});
      }
    } catch (error) {
      console.error("Error submitting application:", error);
    }
  };

  const handleRetrieveChange = (e) => {
    const { name, value } = e.target;
    setRetrieveData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRetrieve = async (e) => {
    e.preventDefault();
    setRetrieveError("");
    setRetrieveSuccess("");
    setRetrievedApplication(true);

    try {
      const { ApplicationID, DateOfBirth, PhoneNumber } = retrieveData;

      const response = await axios.get(`${API_URL}/api/RetriveStudentApplication`, {
        params: { ApplicationID, DateOfBirth, PhoneNumber },
      });

      if (response.data) {
        const app = response.data.application;
      
        app.DateOfBirth = formatDateForInput(app.DateOfBirth);
      
        setFormData({
          ...initialFormData,
          ...app,
          IsHostelFacilityRequired: String(app.IsHostelFacilityRequired ?? "0"),
        });
      
        setEducationDetails(
          response.data.educationDetails?.length
            ? response.data.educationDetails
            : [
                { ...emptyEducationRow, EducationLevel: "Tenth" },
                { ...emptyEducationRow, EducationLevel: "Intermediate" },
              ]
        );
      
        setCompetitiveExams(
          response.data.competitiveExams?.length
            ? response.data.competitiveExams
            : [{ ...emptyExamRow }]
        );
      
        setRetrieveSuccess("Application retrieved successfully!");
      
        if (app.PassportPhoto) {
          setPassportPreview(`${API_UPLOAD_URL}/uploads/${app.PassportPhoto}`);
        } else {
          setPassportPreview(null);
        }
      
        try {
          const campusResp = await axios.get(
            `${API_URL}/api/FSAValidateCampus/${app.CampusCode}`
          );
          setSubCampusCodes(campusResp.data.subcampuses || []);
        
          const degreeResp = await axios.get(
            `${API_URL}/api/FSAValidateSubCampus/${app.CampusCode}/${app.SubCampusCode}`
          );
          setDegreeCodes(degreeResp.data.degrees || []);
        
          const branchResp = await axios.get(
            `${API_URL}/api/FSAValidateDegree/${app.CampusCode}/${app.DegreeCode}`
          );
          setBranchCodes(branchResp.data.branches || []);
        } catch (lookupError) {
          console.error("Error loading dropdown values:", lookupError);
        }
      
        if (app.Zip && /^\d{6}$/.test(app.Zip)) {
          await fetchPincodeDetails(app.Zip);
        }
      }

    } catch (error) {
      console.error("Error retrieving application:", error);
      setRetrievedApplication(false);
      setRetrieveError("No matching application found. Please check your details.");
    }
  };

  const getAcademicYearStarts = (past = 1, future = 1) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const currentStart = month < 6 ? year - 1 : year;

    const starts = [];
    for (let y = currentStart - past; y <= currentStart + future; y++) {
      starts.push(y);
    }
    return starts;
  };

  const academicYearStarts = getAcademicYearStarts();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
    <Box sx={{ bgcolor: PAGE_BG, minHeight: "100vh", p: { xs: 1.5, md: 3 } }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          border: `1px solid ${BORDER}`,
          boxShadow: "0 18px 50px rgba(15,23,42,0.08)",
          bgcolor: "#fff",
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 2.5,
            background: `linear-gradient(135deg, ${THEME_ORANGE}, #f39a58)`,
            color: "#fff",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff" }}>
                <AssignmentIcon />
              </Avatar>
              <Box>
                <Typography sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 900 }}>
                  Student Application
                </Typography>
                <Typography sx={{ opacity: 0.9, fontSize: 13 }}>
                  Fill, save progress, retrieve draft, and submit student admission details.
                </Typography>
              </Box>
            </Stack>

            <Chip
              icon={<InfoOutlinedIcon sx={{ color: "#fff !important" }} />}
              label="Required fields are marked automatically"
              sx={{
                color: "#fff",
                bgcolor: "rgba(255,255,255,0.16)",
                border: "1px solid rgba(255,255,255,0.25)",
                fontWeight: 700,
              }}
            />
          </Stack>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {success && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
              {success}
            </Alert>
          )}

          <Card
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 3,
              border: `1px solid ${BORDER}`,
              bgcolor: "#FBFDFF",
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: SOFT_BLUE, color: THEME_BLUE }}>
                  <SearchIcon />
                </Avatar>
                <Box>
                  <Typography sx={{ fontWeight: 900, color: THEME_BLUE }}>
                    Retrieve Saved Application
                  </Typography>
                  <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
                    Continue an existing draft using Application ID, DOB, and phone number.
                  </Typography>
                </Box>
              </Stack>

              <form onSubmit={handleRetrieve}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Application ID"
                      name="ApplicationID"
                      value={retrieveData.ApplicationID}
                      onChange={handleRetrieveChange}
                      fullWidth
                      required
                      sx={inputSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BadgeIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <DatePicker
                      label="Date of Birth"
                      format="YYYY-MM-DD"
                      value={retrieveData.DateOfBirth ? dayjs(retrieveData.DateOfBirth) : null}
                      onChange={(newValue) => {
                        setRetrieveData((prev) => ({
                          ...prev,
                          DateOfBirth: newValue ? newValue.format("YYYY-MM-DD") : "",
                        }));
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          sx: inputSx,
                          InputProps: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarMonthIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Phone Number"
                      name="PhoneNumber"
                      value={retrieveData.PhoneNumber}
                      onChange={handleRetrieveChange}
                      fullWidth
                      required
                      sx={inputSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      startIcon={<SearchIcon />}
                      sx={{
                        height: "100%",
                        minHeight: 54,
                        borderRadius: 2,
                        bgcolor: THEME_BLUE,
                        fontWeight: 800,
                        textTransform: "none",
                        "&:hover": { bgcolor: "#003b73" },
                      }}
                    >
                      Retrieve Application
                    </Button>
                  </Grid>

                  {retrieveError && (
                    <Grid item xs={12}>
                      <Alert severity="error" sx={{ borderRadius: 2 }}>
                        {retrieveError}
                      </Alert>
                    </Grid>
                  )}

                  {retrieveSuccess && (
                    <Grid item xs={12}>
                      <Alert severity="success" sx={{ borderRadius: 2 }}>
                        {retrieveSuccess}
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </form>
            </CardContent>
          </Card>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2.5}>
              <SectionHeader
                icon={<SchoolIcon />}
                title="Application Details"
                subtitle="Campus, program, academic year, and generated application ID."
              />

              <Grid item xs={12} sm={3}>
                <TextField
                  label="Campus Code"
                  name="CampusCode"
                  value={formData.CampusCode || loginCampusCode}
                  fullWidth
                  error={!!errorstate.CampusCode}
                  helperText={errorstate.CampusCode || "Campus is taken from logged-in user."}
                  required
                  disabled
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ApartmentIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                {/*
                <TextField
                  label="Campus Code"
                  name="CampusCode"
                  value={formData.CampusCode}
                  onChange={handleChange}
                  fullWidth
                  error={!!errorstate.CampusCode}
                  helperText={
                    errorstate.CampusCode ||
                    campusError ||
                    "Check college website for valid Campus Code."
                  }
                  required
                  disabled={retrievedApplication}
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ApartmentIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                */}
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormControl
                  fullWidth
                  disabled={!subcampusCodes.length || retrievedApplication}
                  error={!!errorstate.SubCampusCode}
                  required
                  sx={inputSx}
                >
                  <InputLabel>Sub Campus Code</InputLabel>
                  <Select
                    label="Sub Campus Code"
                    name="SubCampusCode"
                    value={formData.SubCampusCode}
                    onChange={handleChange}
                    sx={selectSx}
                  >
                    {subcampusCodes.map((subcampus) => (
                      <MenuItem
                        key={subcampus.SubCampusCode}
                        value={subcampus.SubCampusCode}
                      >
                        {subcampus.SubCampusCode}
                      </MenuItem>
                    ))}
                  </Select>
                  {errorstate.SubCampusCode && (
                    <FormHelperText>{errorstate.SubCampusCode}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormControl
                  fullWidth
                  disabled={degreeCodes.length === 0 || retrievedApplication}
                  error={!!errorstate.DegreeCode}
                  required
                  sx={inputSx}
                >
                  <InputLabel>Degree Code</InputLabel>
                  <Select
                    label="Degree Code"
                    name="DegreeCode"
                    value={formData.DegreeCode}
                    onChange={handleChange}
                    sx={selectSx}
                  >
                    {degreeCodes.map((degree) => (
                      <MenuItem key={degree.DegreeCode} value={degree.DegreeCode}>
                        {degree.DegreeCode}
                      </MenuItem>
                    ))}
                  </Select>
                  {errorstate.DegreeCode && (
                    <FormHelperText>{errorstate.DegreeCode}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormControl
                  fullWidth
                  disabled={branchCodes.length === 0 || retrievedApplication}
                  error={!!errorstate.BranchCode}
                  required
                  sx={inputSx}
                >
                  <InputLabel>Branch Code</InputLabel>
                  <Select
                    label="Branch Code"
                    name="BranchCode"
                    value={formData.BranchCode}
                    onChange={handleChange}
                    sx={selectSx}
                  >
                    {branchCodes.map((branch) => (
                      <MenuItem key={branch.BranchCode} value={branch.BranchCode}>
                        {branch.BranchCode}
                      </MenuItem>
                    ))}
                  </Select>
                  {errorstate.BranchCode && (
                    <FormHelperText>{errorstate.BranchCode}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Application ID"
                  name="ApplicationID"
                  value={formData.ApplicationID}
                  onChange={handleChange}
                  fullWidth
                  helperText="Auto generated after Branch Code is selected."
                  disabled
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BadgeIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth required sx={inputSx}>
                  <InputLabel>Academic Year</InputLabel>
                  <Select
                    label="Academic Year"
                    name="AcademicYear"
                    value={formData.AcademicYear || ""}
                    onChange={handleChange}
                    sx={selectSx}
                  >
                    {lookupAcademicYears.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                        {year === currentAcademicYear ? "  Current" : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/*
                <FormControl fullWidth sx={inputSx}>
                  <InputLabel>Academic Year</InputLabel>
                  <Select
                    label="Academic Year"
                    name="AcademicYear"
                    value={formData.AcademicYear || ""}
                    onChange={handleChange}
                    sx={selectSx}
                  >
                    {academicYearStarts.map((start) => (
                      <MenuItem key={start} value={start}>
                        {start}-{start + 1}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                */}
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth sx={inputSx}>
                  <InputLabel>Is Hostel Facility Required?</InputLabel>
                  <Select
                    label="Is Hostel Facility Required?"
                    name="IsHostelFacilityRequired"
                    value={formData.IsHostelFacilityRequired}
                    onChange={handleChange}
                    required
                    sx={selectSx}
                  >
                    <MenuItem value="1">Yes</MenuItem>
                    <MenuItem value="0">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <SectionHeader
                icon={<PersonIcon />}
                title="Personal Details"
                subtitle="Student identity and basic profile information."
              />

              <Grid item xs={12} sm={3}>
                <TextField
                  label="First Name"
                  name="FirstName"
                  value={formData.FirstName}
                  onChange={handleChange}
                  error={!!errorstate.FirstName}
                  helperText={errorstate.FirstName}
                  fullWidth
                  required
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  label="Family Name"
                  name="FamilyName"
                  value={formData.FamilyName}
                  onChange={handleChange}
                  error={!!errorstate.FamilyName}
                  helperText={errorstate.FamilyName}
                  fullWidth
                  required
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormControl fullWidth sx={inputSx}>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    label="Gender"
                    name="Gender"
                    value={formData.Gender}
                    onChange={handleChange}
                    required
                    sx={selectSx}
                  >
                    <MenuItem value="M">Male</MenuItem>
                    <MenuItem value="F">Female</MenuItem>
                    <MenuItem value="O">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormControl fullWidth sx={inputSx}>
                  <InputLabel>Blood Group</InputLabel>
                  <Select
                    label="Blood Group"
                    name="BloodGroup"
                    value={formData.BloodGroup}
                    onChange={handleChange}
                    required
                    sx={selectSx}
                  >
                    {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map(
                      (blood) => (
                        <MenuItem key={blood} value={blood}>
                          {blood}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <DatePicker
                  label="Date of Birth"
                  format="YYYY-MM-DD"
                  value={formData.DateOfBirth ? dayjs(formData.DateOfBirth) : null}
                  onChange={(newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      DateOfBirth: newValue ? newValue.format("YYYY-MM-DD") : "",
                    }));
                  }}
                  disabled={retrievedApplication}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !!errorstate.DateOfBirth,
                      helperText: errorstate.DateOfBirth,
                      sx: inputSx,
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarMonthIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  label="Religion"
                  name="Religion"
                  value={formData.Religion}
                  onChange={handleChange}
                  fullWidth
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  label="Caste"
                  name="Caste"
                  value={formData.Caste}
                  onChange={handleChange}
                  fullWidth
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  label="Sub Caste"
                  name="SubCaste"
                  value={formData.SubCaste}
                  onChange={handleChange}
                  fullWidth
                  sx={inputSx}
                />
              </Grid>

              <SectionHeader
                icon={<ContactPhoneIcon />}
                title="Contact Details"
                subtitle="Address, phone number, email, and identity details."
              />

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Address Line 1"
                  name="AddressLine1"
                  value={formData.AddressLine1}
                  onChange={handleChange}
                  fullWidth
                  required
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <HomeIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Address Line 2"
                  name="AddressLine2"
                  value={formData.AddressLine2}
                  onChange={handleChange}
                  fullWidth
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={1.5}>
                <TextField
                  label="ZIP / PIN Code"
                  name="Zip"
                  value={formData.Zip}
                  onChange={handleChange}
                  fullWidth
                  required
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={2.5}>
                <Autocomplete
                  freeSolo
                  options={pincodeRecords}
                  disabled={!formData.Zip || formData.Zip.length !== 6}
                  getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    return option.PostOfficeName || "";
                  }}
                  value={
                    pincodeRecords.find(
                      (r) => r.PostOfficeName === formData.PostOfficeName
                    ) || formData.PostOfficeName || null
                  }
                  inputValue={formData.PostOfficeName || ""}                  
                  onInputChange={(event, newInputValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      PostOfficeName: newInputValue,
                    }));
                  
                    const matched = pincodeRecords.find(
                      (r) =>
                        String(r.PostOfficeName || "").toLowerCase() ===
                        String(newInputValue || "").toLowerCase()
                    );
                  
                    if (matched) {
                      setIsCustomLocality(false);
                      setFormData((prev) => ({
                        ...prev,
                        PostOfficeName: matched.PostOfficeName || "",
                        District: matched.DistrictName || "",
                        State: matched.StateName || "",
                      }));
                    } else {
                      setIsCustomLocality(true);
                    }
                  }}
                  onChange={(event, selected) => {
                    if (!selected) return;
                  
                    if (typeof selected === "string") {
                      setIsCustomLocality(true);
                      setFormData((prev) => ({
                        ...prev,
                        PostOfficeName: selected,
                      }));
                      return;
                    }
                  
                    setIsCustomLocality(false);
                    setFormData((prev) => ({
                      ...prev,
                      PostOfficeName: selected.PostOfficeName || "",
                      District: selected.DistrictName || "",
                      State: selected.StateName || "",
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Post Office / Locality"
                      required
                      sx={inputSx}
                      helperText={
                        isCustomLocality
                          ? "New locality. City, district and state are editable."
                          : "Select existing locality or type a new one."
                      }
                    />
                  )}
                />
              </Grid>
                
              <Grid item xs={12} sm={2.5}>
                <TextField
                  label="City"
                  name="City"
                  value={formData.City}
                  onChange={handleChange}
                  fullWidth
                  required
                  sx={inputSx}
                />
              </Grid>
                
              <Grid item xs={12} sm={2.5}>
                <TextField
                  label="District"
                  name="District"
                  value={formData.District}
                  onChange={handleChange}
                  fullWidth
                  required
                  InputProps={{
                    readOnly: !isCustomLocality && pincodeRecords.length > 0,
                  }}
                  sx={inputSx}
                />
              </Grid>
                
              <Grid item xs={12} sm={3}>
                <TextField
                  label="State"
                  name="State"
                  value={formData.State}
                  onChange={handleChange}
                  fullWidth
                  required
                  InputProps={{
                    readOnly: !isCustomLocality && pincodeRecords.length > 0,
                  }}
                  sx={inputSx}
                />
              </Grid>                    

              <Grid item xs={12} sm={3}>
                <TextField
                  label="Aadhar Number"
                  name="AadharNumber"
                  value={formData.AadharNumber}
                  onChange={handleChange}
                  fullWidth
                  error={!!errorstate.AadharNumber}
                  helperText={errorstate.AadharNumber || "Enter exactly 12 digits."}
                  required
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <NumbersIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  label="Phone Number"
                  name="PhoneNumber"
                  value={formData.PhoneNumber}
                  onChange={handleChange}
                  fullWidth
                  error={!!errorstate.PhoneNumber}
                  helperText={errorstate.PhoneNumber || "Enter exactly 10 digits."}
                  required
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  label="Alternate Phone Number"
                  name="AlternatePhoneNumber"
                  value={formData.AlternatePhoneNumber}
                  onChange={handleChange}
                  fullWidth
                  error={!!errorstate.AlternatePhoneNumber}
                  helperText={
                    errorstate.AlternatePhoneNumber || "Enter exactly 10 digits."
                  }
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  label="Email Address"
                  name="EmailAddress"
                  value={formData.EmailAddress}
                  onChange={handleChange}
                  fullWidth
                  required
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <SectionHeader
                icon={<FamilyRestroomIcon />}
                title="Family Details"
                subtitle="Parent / guardian information required for admission records."
              />

              {[
                ["Father Full Name", "FatherFullName", true],
                ["Father Aadhar Number", "FatherAadharNumber", true],
                ["Father Qualification", "FatherQualification", true],
                ["Father Occupation", "FatherOccupation", true],
                ["Father Phone Number", "FatherPhoneNumber", true],
                ["Mother Full Name", "MotherFullName", true],
                ["Mother Aadhar Number", "MotherAadharNumber", true],
                ["Mother Qualification", "MotherQualification", true],
                ["Mother Occupation", "MotherOccupation", true],
                ["Mother Phone Number", "MotherPhoneNumber", true],
              ].map(([label, name, required]) => (
                <Grid item xs={12} sm={2.4} key={name}>
                  <TextField
                    label={label}
                    name={name}
                    value={formData[name]}
                    onChange={handleChange}
                    fullWidth
                    required={required}
                    error={!!errorstate[name]}
                    helperText={
                      errorstate[name] ||
                      (name.includes("Aadhar")
                        ? "Enter exactly 12 digits."
                        : name.includes("Phone")
                        ? "Enter exactly 10 digits."
                        : "")
                    }
                    sx={inputSx}
                  />
                </Grid>
              ))}

              <SectionHeader
                icon={<MenuBookIcon />}
                title="Education Details"
                subtitle="Add Education details."
              />

              {educationDetails.map((row, index) => (
                <Grid item xs={12} key={`education-${index}`}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${BORDER}`,
                      bgcolor: "#FBFDFF",
                    }}
                  >
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={2}>
                          <FormControl fullWidth required sx={inputSx}>
                            <InputLabel>Education Level</InputLabel>
                            <Select
                              label="Education Level"
                              value={row.EducationLevel}
                              onChange={(e) =>
                                handleEducationChange(index, "EducationLevel", e.target.value)
                              }
                              sx={selectSx}
                            >
                              {["Tenth", "Intermediate", "Diploma", "Degree", "PG", "Other"].map(
                                (item) => (
                                  <MenuItem key={item} value={item}>
                                    {item}
                                  </MenuItem>
                                )
                              )}
                            </Select>
                          </FormControl>
                        </Grid>
                            
                        <Grid item xs={12} sm={2}>
                          <FormControl fullWidth sx={inputSx}>
                            <InputLabel>Board Type</InputLabel>
                            <Select
                              label="Board Type"
                              value={row.BoardType || ""}
                              onChange={(e) =>
                                handleEducationChange(index, "BoardType", e.target.value)
                              }
                              sx={selectSx}
                            >
                              {["CBSE", "ICSE", "IB", "State", "University", "Other"].map(
                                (item) => (
                                  <MenuItem key={item} value={item}>
                                    {item}
                                  </MenuItem>
                                )
                              )}
                            </Select>
                          </FormControl>
                        </Grid>
                            
                        <Grid item xs={12} sm={3}>
                          <TextField
                            label="Institution Name"
                            value={row.InstitutionName || ""}
                            onChange={(e) =>
                              handleEducationChange(index, "InstitutionName", e.target.value)
                            }
                            fullWidth
                            required
                            sx={inputSx}
                          />
                        </Grid>
                          
                        <Grid item xs={12} sm={2}>
                          <TextField
                            label="Hall Ticket Number"
                            value={row.HallTicketNumber || ""}
                            onChange={(e) =>
                              handleEducationChange(index, "HallTicketNumber", e.target.value)
                            }
                            fullWidth
                            sx={inputSx}
                          />
                        </Grid>
                          
                        <Grid item xs={12} sm={1.5}>
                          <TextField
                            label="Percentage"
                            value={row.Percentage || ""}
                            onChange={(e) =>
                              handleEducationChange(index, "Percentage", e.target.value)
                            }
                            fullWidth
                            sx={inputSx}
                          />
                        </Grid>
                          
                        <Grid item xs={12} sm={1.5}>
                          <TextField
                            label="CGPA"
                            value={row.CGPA || ""}
                            onChange={(e) =>
                              handleEducationChange(index, "CGPA", e.target.value)
                            }
                            fullWidth
                            sx={inputSx}
                          />
                        </Grid>
                          
                        <Grid item xs={12} sm={3}>
                          <TextField
                            label="City"
                            value={row.InstitutionCity || ""}
                            onChange={(e) =>
                              handleEducationChange(index, "InstitutionCity", e.target.value)
                            }
                            fullWidth
                            sx={inputSx}
                          />
                        </Grid>
                          
                        <Grid item xs={12} sm={3}>
                          <StateSelect
                            label="State"
                            name={`InstitutionState-${index}`}
                            value={row.InstitutionState || ""}
                            onChange={(e) =>
                              handleEducationChange(index, "InstitutionState", e.target.value)
                            }
                          />
                        </Grid>
                          
                        <Grid item xs={12} sm={2}>
                          <TextField
                            label="Passed Out Year"
                            value={row.PassedOutYear || ""}
                            onChange={(e) =>
                              handleEducationChange(index, "PassedOutYear", e.target.value)
                            }
                            fullWidth
                            sx={inputSx}
                          />
                        </Grid>
                          
                        <Grid item xs={12} sm={2}>
                          <Button
                            variant="outlined"
                            color="error"
                            fullWidth
                            startIcon={<DeleteIcon />}
                            onClick={() => removeEducationRow(index)}
                            disabled={educationDetails.length <= 1}
                            sx={{
                              height: 54,
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 800,
                            }}
                          >
                            Remove
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}

              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addEducationRow}
                  sx={{
                    borderRadius: 2,
                    borderColor: THEME_BLUE,
                    color: THEME_BLUE,
                    textTransform: "none",
                    fontWeight: 900,
                  }}
                >
                  Add Education
                </Button>
              </Grid>

              <SectionHeader
                icon={<WorkspacePremiumIcon />}
                title="Competitive / Entrance Exams"
                subtitle="Add Entrance exam details."
              />

              {competitiveExams.map((row, index) => (
                <Grid item xs={12} key={`exam-${index}`}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${BORDER}`,
                      bgcolor: "#FBFDFF",
                    }}
                  >
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            label="Exam Type"
                            value={row.ExamType || ""}
                            onChange={(e) =>
                              handleExamChange(index, "ExamType", e.target.value)
                            }
                            placeholder="EAMCET / JEE / ECET"
                            fullWidth
                            sx={inputSx}
                          />
                        </Grid>
                          
                        <Grid item xs={12} sm={3}>
                          <TextField
                            label="Hall Ticket Number"
                            value={row.HallTicketNumber || ""}
                            onChange={(e) =>
                              handleExamChange(index, "HallTicketNumber", e.target.value)
                            }
                            fullWidth
                            sx={inputSx}
                          />
                        </Grid>
                          
                        <Grid item xs={12} sm={2}>
                          <TextField
                            label="Rank"
                            value={row.RankNo || ""}
                            onChange={(e) =>
                              handleExamChange(index, "RankNo", e.target.value)
                            }
                            fullWidth
                            sx={inputSx}
                          />
                        </Grid>
                          
                        <Grid item xs={12} sm={2}>
                          <TextField
                            label="Score"
                            value={row.Score || ""}
                            onChange={(e) =>
                              handleExamChange(index, "Score", e.target.value)
                            }
                            fullWidth
                            sx={inputSx}
                          />
                        </Grid>
                          
                        <Grid item xs={12} sm={1}>
                          <TextField
                            label="Year"
                            value={row.ExamYear || ""}
                            onChange={(e) =>
                              handleExamChange(index, "ExamYear", e.target.value)
                            }
                            fullWidth
                            sx={inputSx}
                          />
                        </Grid>
                          
                        <Grid item xs={12} sm={1}>
                          <Button
                            variant="outlined"
                            color="error"
                            fullWidth
                            onClick={() => removeExamRow(index)}
                            disabled={competitiveExams.length <= 1}
                            sx={{
                              height: 54,
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 800,
                            }}
                          >
                            Remove
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}

              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addExamRow}
                  sx={{
                    borderRadius: 2,
                    borderColor: THEME_BLUE,
                    color: THEME_BLUE,
                    textTransform: "none",
                    fontWeight: 900,
                  }}
                >
                  Add Competitive Exam
                </Button>
              </Grid>

              <SectionHeader
                icon={<BadgeIcon />}
                title="Reference Details"
                subtitle="Optional reference information."
              />

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Reference Name"
                  name="ReferenceName"
                  value={formData.ReferenceName}
                  onChange={handleChange}
                  fullWidth
                  sx={inputSx}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Reference Phone Number"
                  name="ReferencePhoneNumber"
                  value={formData.ReferencePhoneNumber}
                  onChange={handleChange}
                  fullWidth
                  error={!!errorstate.ReferencePhoneNumber}
                  helperText={
                    errorstate.ReferencePhoneNumber || "Enter exactly 10 digits."
                  }
                  sx={inputSx}
                />
              </Grid>

              <SectionHeader
                icon={<UploadFileIcon />}
                title="Uploads"
                subtitle="Upload passport-size photo. Maximum file size: 2 MB."
              />

              <Grid item xs={12}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: `1px dashed rgba(0,77,153,0.35)`,
                    bgcolor: "#FBFDFF",
                  }}
                >
                  <CardContent>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                          src={passportPreview || ""}
                          variant="rounded"
                          sx={{
                            width: 96,
                            height: 96,
                            bgcolor: SOFT_BLUE,
                            color: THEME_BLUE,
                            border: `1px solid ${BORDER}`,
                          }}
                        >
                          <PhotoCameraIcon />
                        </Avatar>

                        <Box>
                          <Typography sx={{ fontWeight: 900, color: THEME_BLUE }}>
                            Passport Photo
                          </Typography>
                          <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
                            JPG / PNG image under 2 MB.
                          </Typography>
                          {passportPhoto?.name && (
                            <Chip
                              label={passportPhoto.name}
                              size="small"
                              sx={{ mt: 1, bgcolor: SOFT_BLUE }}
                            />
                          )}
                        </Box>
                      </Stack>

                      <Box>
                        <input
                          accept="image/*"
                          type="file"
                          name="passportPhoto"
                          onChange={handlePhotoChange}
                          style={{ display: "none" }}
                          id="upload-passport-photo"
                        />
                        <label htmlFor="upload-passport-photo">
                          <Button
                            variant="contained"
                            component="span"
                            startIcon={<UploadFileIcon />}
                            sx={{
                              borderRadius: 2,
                              bgcolor: THEME_BLUE,
                              fontWeight: 800,
                              textTransform: "none",
                              "&:hover": { bgcolor: "#003b73" },
                            }}
                          >
                            Upload Photo
                          </Button>
                        </label>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1, borderColor: BORDER }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Button
                  onClick={handleSave}
                  variant="outlined"
                  fullWidth
                  startIcon={<SaveIcon />}
                  sx={{
                    py: 1.25,
                    borderRadius: 2,
                    borderColor: THEME_BLUE,
                    color: THEME_BLUE,
                    fontWeight: 900,
                    textTransform: "none",
                    "&:hover": {
                      borderColor: THEME_BLUE,
                      bgcolor: SOFT_BLUE,
                    },
                  }}
                >
                  Save Application
                </Button>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  startIcon={<SendIcon />}
                  sx={{
                    py: 1.25,
                    borderRadius: 2,
                    bgcolor: THEME_ORANGE,
                    fontWeight: 900,
                    textTransform: "none",
                    boxShadow: "0 10px 24px rgba(240,120,40,0.25)",
                    "&:hover": {
                      bgcolor: "#df681f",
                    },
                  }}
                >
                  Submit Application
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Paper>
    </Box>
    </LocalizationProvider>
  );
};

export default FormStudentApplication;